import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AvatarModal.module.scss';
import AvatarImagePreview from './AvatarImagePreview';
import AvatarVoicePreview from './AvatarVoicePreview';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../components/Button/Button';
import ContainerWithScroll from '../../../../components/ContainerWithScroll/ContainerWithScroll';
import Spinner from '../../../../../common/components/Spinner';
import VideoService from '../../../../service/VideoService';
import RadioButton from 'engage/components/RadioButton/RadioButton';
import cx from 'classnames';
import ClipsList from 'engage/components/ClipsList/ClipsList';
import Icon from 'engage/components/Icon/Icon';

const AvatarModal = ({ scenes, sceneId = null, onClose, onSelect }) => {
    const [imagesList, setImagesList] = useState([]);
    const [voicesList, setVoicesList] = useState([]);

    const [applyToAll, setApplyToAll] = useState(sceneId == null);

    const [loading, setLoading] = useState(false);

    const scrollContainerRef = useRef(null);

    const [currentSceneIndex, setCurrentSceneIndex] = useState(scenes?.findIndex(scene => sceneId ? scene.id == sceneId : scene.script.length > 0));

    const currentClip = useMemo(() => currentSceneIndex.toString(), [currentSceneIndex]);

    const audioRef = useRef(null);
    const [currentPlayingVoiceId, setCurrentPlayingVoiceId] = useState(null);
    const [selectedAvatars, setSelectedAvatars] = useState({});

    const { avatarId = null, voiceId = null } = useMemo(() => {
        if (applyToAll) {
            const { avatarId = null, voiceId = null } = selectedAvatars || {};
            return { avatarId, voiceId };
        } else {
            const { avatarId = null, voiceId = null } = selectedAvatars[scenes[currentSceneIndex]?.id] || {};
            return { avatarId, voiceId };
        }
    }, [selectedAvatars, currentSceneIndex, applyToAll, scenes]);

    const { t } = useTranslation();

    const getSceneInfo = useCallback((scene) => {
        if (!applyToAll && scenes.length > 1) {
            let icon = null;
            const content = [];
            const emptyScript = scene?.script?.length == 0;
            if (emptyScript) {
                content.push(t('clip_organizer.generate_avatar.no_script'));
            }
            const hasRecordedVideo = scene.video != null;
            if (hasRecordedVideo && !emptyScript) {
                icon = <div style={{ width: '16px', height: '16px' }}>⚠️</div>;
                content.push('⚠️ ' + t('clip_organizer.generate_avatar.scene_has_video'));
            }
            const bothSelected = selectedAvatars[scene.id]?.avatarId && selectedAvatars[scene.id]?.voiceId;
            if (bothSelected) {
                icon = <div className={styles.bothSelected}>
                    <Icon src={'checked'} className={styles.icon} />
                </div>;
                content.push('✅ ' + t('clip_organizer.generate_avatar.selected'));
            }
            return { text: content.join('\n'), icon };
        }
        return null;
    }, [scenes, applyToAll, selectedAvatars])

    const isReadyToGenerate = useMemo(() => {
        if (loading) {
            return false;
        }
        if (applyToAll) {
            return selectedAvatars?.avatarId && selectedAvatars?.voiceId;
        }
        const sceneEntries = Object.entries(selectedAvatars).filter(([_, value]) => typeof value == 'object');
        if (sceneEntries.length === 0) {
            return false;
        }
        return sceneEntries.every(([_, value]) => value?.avatarId && value?.voiceId);
    }, [loading, applyToAll, selectedAvatars]);

    const handleSelectData = (key, id, currentSceneId, applyToAll) => {
        if (!applyToAll) {
            setSelectedAvatars(prevState => {
                if (prevState[currentSceneId]) {
                    if (prevState[currentSceneId] && prevState[currentSceneId][key] === id) {
                        return { ...prevState, [currentSceneId]: { [key]: null } }
                    } else {
                        return { ...prevState, [currentSceneId]: { ...prevState[currentSceneId], [key]: id } }
                    }
                } else {
                    return { ...prevState, [currentSceneId]: { [key]: id } }
                }
            });
            return;
        }
        setSelectedAvatars(prevState => ({ ...prevState, [key]: id == prevState[key] ? null : id }));
    }

    const handleSelectImage = useCallback((avatarId) => {
        handleSelectData('avatarId', avatarId, scenes[currentSceneIndex].id, applyToAll);
    }, [applyToAll, currentSceneIndex, scenes]);

    const handleSelectAudio = useCallback((voiceId) => {
        handleSelectData('voiceId', voiceId, scenes[currentSceneIndex].id, applyToAll);
    }, [applyToAll, currentSceneIndex, scenes]);

    const handleClose = () => {
        setSelectedAvatars({});
        onClose();
    }

    const handleSelectClip = (value) => {
        setCurrentSceneIndex(Number(value));
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const handleSelect = useCallback(async () => {
        try {
            setLoading(true);
            const dataForGeneration = [];
            if (applyToAll) {
                const { avatarId, voiceId } = selectedAvatars;
                dataForGeneration.push({ avatarId, voiceId });
            } else {
                const scenes = Object.entries(selectedAvatars).map(([sceneId, { avatarId, voiceId }]) => ({ sceneId, avatarId, voiceId }));
                dataForGeneration.push(...scenes);
            }
            await onSelect(dataForGeneration);
        } catch (e) {
            console.log('Error avatar generation', e);
        } finally {
            setLoading(false);
            handleClose();
        }
    }, [applyToAll, selectedAvatars]);

    const handlePlayPauseVoice = useCallback((id) => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.addEventListener('ended', () => {
                console.log('ended');
                setCurrentPlayingVoiceId(null);
            });
        }
        if (currentPlayingVoiceId === id) {
            if (audioRef.current.paused) {
                audioRef.current.play()
            } else {
                audioRef.current.pause()
                setCurrentPlayingVoiceId(null);
                audioRef.current.currentTime = 0;
            }
        } else {
            !audioRef.current.paused && audioRef.current.pause();
            const voiceSource = voicesList.find(voice => voice.voice_id === id).preview_audio;
            audioRef.current.src = voiceSource;
            setCurrentPlayingVoiceId(id);
            audioRef.current.play();
        }
    }, [currentPlayingVoiceId, voicesList]);

    useEffect(() => {

        const loadResources = async () => {
            const avatars = await VideoService.getAvatarsTemplates();
            setImagesList(avatars.avatars);
            setVoicesList(avatars.voices);
        }

        loadResources();

        return () => {
            !audioRef?.current?.paused && audioRef.current?.pause();
        }
    }, [])

    return <div className={styles.avatarModal} style={{ maxHeight: `calc(${window.innerHeight}px - 230px)`, height: loading ? '100%' : 'fit-content' }}>
        {loading ? <div className={styles.loading}>
            <Spinner width='40px' height='40px' size='3px' className={styles.spinner} />
            <h1 className={styles.title}>{t('clip_organizer.generate_avatar.loading')}</h1>
        </div> : <>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    {t('clip_organizer.generate_avatar.modal_header')}
                    <span className={styles.betaBadge}>BETA</span>
                </h1>
                <CloseButton onClose={handleClose} />
            </div>
            {scenes.length > 1 && <>
                <RadioButton checked={applyToAll} onChange={(e) => setApplyToAll(e.target.checked)} label={t('clip_organizer.generate_avatar.apply_to_all')} className={styles.applyToAllCheckbox} />
                <div className={cx(styles.warningContainer, { [styles.show]: applyToAll && scenes.filter(item => item?.script?.length).some(item => item.video != null) })}>
                    <p className={styles.warning}>{t('clip_organizer.generate_avatar.apply_to_all.warning')}</p>
                </div>
            </>}
            {scenes.length > 1 && !applyToAll &&
                <ClipsList className={styles.scenesList} cards={scenes.map(scene => ({ ...scene, disabled: !scene.script.length, info: getSceneInfo(scene) }))} handleSelectClip={handleSelectClip} currentVideoPart={currentClip} isEditingAction={false} />}
            <ContainerWithScroll ref={scrollContainerRef} className={styles.dataList}>
                <h2 className={styles.sectionTitle}>{t('clip_organizer.generate_avatar.select_image')}</h2>
                {<div className={cx(styles.warningContainer, { [styles.show]: !applyToAll && scenes[currentSceneIndex]?.video != null })}>
                    <p className={styles.warning}>{t('clip_organizer.generate_avatar.scene_recorded.warning')}</p>
                </div>}
                <div className={styles.section} role='listbox' aria-label="Select an avatar image" tabIndex="0">
                    {imagesList?.map((avatar, index) => <AvatarImagePreview key={index} avatar={avatar} onSelect={handleSelectImage} selected={avatar.avatar_id === avatarId} />)}
                </div>
                <h2 className={styles.sectionTitle}>{t('clip_organizer.generate_avatar.select_voice')}</h2>
                <div className={styles.voiceSection}>
                    {voicesList?.map((voice, index) => <AvatarVoicePreview key={index} voice={voice} onSelect={handleSelectAudio} selected={voice.voice_id === voiceId} playPauseVoice={handlePlayPauseVoice} currentPlayingVoiceId={currentPlayingVoiceId} />)}
                </div>
            </ContainerWithScroll>
            <div className={styles.footer}>
                <CustomButton label={t('general.cancel')} variant='secondary' onClick={handleClose} />
                <CustomButton label={t('subtitles.editor.generate')} variant='primary' onClick={handleSelect} disabled={!isReadyToGenerate} />
            </div></>}
    </div>
}

export default AvatarModal;